import React, { createContext, MutableRefObject, ReactNode, useEffect, useRef, useState } from 'react'
import { useCreationCropType } from '../../hooks/use-creation-crop-type'
import { CreationToolSourceFile } from '../../types'
import CreationToolCrop from '../../types/crop'
import CreationToolFont from '../../types/font'
import CreationToolFontSize from '../../types/font-size'
import CreationToolOutputType from '../../types/output-type'
import CreationToolSourceEditor from '../../types/source-editor'
import { ArtboardRef } from '../artboard/types'
import { useEdits } from './hooks/use-edits'
import {
    SourceEditorCaption,
    SourceEditorCaptionStyle,
    SourceEditorCrop,
    SourceEditorEdits,
    SourceEditorTimeline,
    SourceEditorWatermark,
} from './types'

export type CreationSourceEditorContextProps = {
    source: CreationToolSourceFile
    edits?: SourceEditorEdits
    currentEditor: CreationToolSourceEditor
    captionStyle: SourceEditorCaptionStyle
    captions: SourceEditorCaption[]
    crop: SourceEditorCrop
    timeline: SourceEditorTimeline
    cropType: CreationToolCrop
    outputType: CreationToolOutputType
    artboardRef: MutableRefObject<ArtboardRef | null>
    customFontUrl?: string
    watermark?: SourceEditorWatermark
    previewPlayheadTime: number
    setCurrentEditor: (editor: CreationToolSourceEditor) => void
    setCaptionStyle: (style: SourceEditorCaptionStyle) => void
    setCaptions: (captions: SourceEditorCaption[]) => void
    setCrop: (crop: SourceEditorCrop) => void
    setTimeline: (timeline: SourceEditorTimeline) => void
    setCropType: (cropType: CreationToolCrop) => void
    setPreviewPlayheadTime: (playheadTime: number) => void
}

export const CreationSourceEditorContext = createContext({} as CreationSourceEditorContextProps)

type Props = {
    defaultEditor?: CreationToolSourceEditor
    defaultEdits?: SourceEditorEdits
    customFontUrl?: string
    watermark?: SourceEditorWatermark
    source: CreationToolSourceFile
    outputType: CreationToolOutputType
    children: ReactNode
    onUpdate?: (edits?: SourceEditorEdits) => void
}

const CreationSourceEditorContextManager = ({
    defaultEditor = CreationToolSourceEditor.Edit,
    customFontUrl,
    watermark,
    defaultEdits,
    outputType,
    source,
    children,
    onUpdate,
}: Props) => {
    const defaultCropType = useCreationCropType(
        defaultEdits?.crop?.width || source.width,
        defaultEdits?.crop?.height || source.height,
        source.width / source.height
    )

    const [currentEditor, setCurrentEditor] = useState<CreationToolSourceEditor>(defaultEditor)
    const [cropType, setCropType] = useState<CreationToolCrop>(defaultCropType)
    const [captions, setCaptions] = useState<SourceEditorCaption[]>(defaultEdits?.captions || [])
    const [previewPlayheadTime, setPreviewPlayheadTime] = useState<number>(0)
    const artboardRef = useRef<ArtboardRef | null>(null)

    const [timeline, setTimeline] = useState<SourceEditorTimeline>(
        defaultEdits?.trim || {
            start: 0,
            end: source.duration,
        }
    )

    const [crop, setCrop] = useState<SourceEditorCrop>(
        defaultEdits?.crop || {
            x: 0,
            y: 0,
            width: source.width,
            height: source.height,
        }
    )

    const [captionStyle, setCaptionStyle] = useState<SourceEditorCaptionStyle>({
        color: '#fff',
        font: customFontUrl ? CreationToolFont.Custom : CreationToolFont.Giphy,
        fontSize: CreationToolFontSize.Medium,
    })

    const edits = useEdits({ source, timeline, captions, crop })

    useEffect(() => {
        if (!onUpdate) return

        if (!edits) {
            onUpdate(undefined)
        } else {
            const editKeys = Object.keys(edits)
            const cleanedEdits: SourceEditorEdits = {}
            editKeys.forEach(key => {
                if (Array.isArray(edits[key])) {
                    if (edits[key].length) {
                        cleanedEdits[key] = edits[key]
                    }
                } else if (edits[key] !== undefined) {
                    cleanedEdits[key] = edits[key]
                }
            })
            onUpdate(cleanedEdits)
        }
    }, [edits, onUpdate])

    return (
        <CreationSourceEditorContext.Provider
            value={{
                source,
                currentEditor,
                captionStyle,
                captions,
                crop,
                timeline,
                outputType,
                cropType,
                artboardRef,
                customFontUrl,
                watermark,
                edits,
                previewPlayheadTime,
                setCurrentEditor,
                setCaptionStyle,
                setCaptions,
                setCrop,
                setTimeline,
                setCropType,
                setPreviewPlayheadTime,
            }}
        >
            {children}
        </CreationSourceEditorContext.Provider>
    )
}

export default CreationSourceEditorContextManager
